
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  DatepickerComponent,
  DialogService,
  EmptyComponent,
  InputComponent,
  SearchComponent,
  SelectComponent,
  TableComponent,
  type ActionEvent,
  type SortChangeEvent,
} from 'nebula-ui-kit';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  openVaultConfirmDialog,
  openVaultReasonConfirmDialog,
  scrollVaultEditPanelWhenReady,
  VaultFormValidationAlertComponent,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import { VAULT_TABLE_PAGE_SIZE_OPTIONS } from '@nebula/shared-vault/data-access';
import {
  retentionStore,
  type PendingRetentionAction,
  type RetentionDocumentRow,
  type RetentionPolicyForm,
  type RetentionPolicyRow,
} from '../../../../store/src/lib/retention.store';
import { RetentionCalendarComponent } from './retention-calendar.component';
import {
  retentionDocumentConfirmConfig,
  retentionPolicyConfirmConfig,
} from './retention-dialog.util';
import { retentionEditRecordIdHint } from './retention-edit-id.util';
import { buildRetentionPolicyDetailFields } from './retention-view.utils';

@Component({
  selector: 'app-retention',
  templateUrl: './retention.component.html',
  styleUrls: ['./retention.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    VaultModuleShellComponent,
    InputComponent,
    SelectComponent,
    ButtonComponent,
    CheckboxComponent,
    DatepickerComponent,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    VaultFormValidationAlertComponent,
    SearchComponent,
    TableComponent,
    BadgeComponent,
    EmptyComponent,
    RetentionCalendarComponent,
    VaultPageAlertComponent,
  ],
})
export class RetentionComponent {
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly editPanel = viewChild<ElementRef<HTMLElement>>('editPanel');

  readonly policiesTablePageSize = createVaultTablePageSizeSignal();
  readonly documentsTablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  readonly store = inject(retentionStore);
  readonly loading = this.store.loading;
  readonly saving = this.store.saving;
  readonly error = this.store.error;
  readonly info = this.store.info;
  readonly activeSection = this.store.activeSection;
  readonly searchTerm = this.store.searchTerm;
  readonly retentionSearchTerm = this.store.retentionSearchTerm;
  readonly retentionStatusFilter = this.store.retentionStatusFilter;
  readonly sortColumn = this.store.sortColumn;
  readonly sortDirection = this.store.sortDirection;
  readonly expiringDocs = this.store.expiringDocs;
  readonly editingId = this.store.editingId;
  readonly creating = this.store.creating;
  readonly retentionEditRecordIdHint = retentionEditRecordIdHint;
  readonly createForm = this.store.createForm;
  readonly createFormResetKey = this.store.createFormResetKey;
  readonly editForm = this.store.editForm;
  readonly headerActions = this.store.headerActions;
  readonly tableConfig = this.store.tableConfig;
  readonly documentsTableConfig = this.store.documentsTableConfig;
  readonly filteredPolicies = this.store.filteredPolicies;
  readonly filteredRetentions = this.store.filteredRetentions;
  readonly selectedPolicy = this.store.selectedPolicy;
  readonly documentTypeOptions = this.store.documentTypeOptions();
  readonly unitOptions = this.store.unitOptions();
  readonly actionOptions = this.store.actionOptions();
  readonly retentionStatusOptions = this.store.retentionStatusOptions();

  constructor() {
    this.store.init();
    bindVaultTablePageSizeToPreferences(this.policiesTablePageSize, this.destroyRef);
    bindVaultTablePageSizeToPreferences(this.documentsTablePageSize, this.destroyRef);
  }

  onHeaderAction(actionId: string): void {
    this.store.onHeaderAction(actionId);
  }

  onSearchTermChange(value: string): void {
    this.store.onSearchTermChange(value);
  }

  onSortChange(ev: unknown): void {
    this.store.onSortChange(ev as SortChangeEvent<RetentionPolicyRow>);
  }

  onPoliciesTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) {
      this.policiesTablePageSize.set(ev.pageSize);
    }
  }

  onDocumentsTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) {
      this.documentsTablePageSize.set(ev.pageSize);
    }
  }

  setActiveSection(section: 'policies' | 'documents' | 'expiring'): void {
    this.store.setActiveSection(section);
  }

  onRetentionSearchTermChange(value: string): void {
    this.store.onRetentionSearchTermChange(value);
  }

  onRetentionStatusFilterChange(value: string): void {
    this.store.onRetentionStatusFilterChange(value);
  }

  onCreateField<K extends keyof RetentionPolicyForm>(key: K, value: RetentionPolicyForm[K]): void {
    this.store.onCreateField(key, value);
  }

  onEditField<K extends keyof RetentionPolicyForm>(key: K, value: RetentionPolicyForm[K]): void {
    this.store.onEditField(key, value);
  }

  createPolicy(): void {
    this.store.createPolicy();
  }

  closeCreatePanel(): void {
    this.store.closeCreatePanel();
  }

  onTableAction(ev: unknown): void {
    const event = ev as ActionEvent<RetentionPolicyRow>;
    this.handlePolicyRowAction(event.action, event?.data);
  }

  onPolicyRowAction(action: string, row: RetentionPolicyRow): void {
    this.handlePolicyRowAction(action, row);
  }

  private handlePolicyRowAction(action: string | undefined, row: RetentionPolicyRow | undefined): void {
    if (!row || !action) return;

    if (action === 'view') {
      this.store.openPolicyDetail(row);
      return;
    }

    if (action === 'edit') {
      this.startEdit(row);
      return;
    }

    if (action === 'toggle') {
      const action = row.is_active ? 'deactivate' : 'activate';
      const pending: PendingRetentionAction = {
        id: row.id,
        action,
        name: row.name || row.id,
      };
      openVaultConfirmDialog(
        this.dialogService,
        retentionPolicyConfirmConfig(action, pending.name),
      ).subscribe((confirmed) => {
        if (confirmed) {
          this.store.executeRetentionAction(pending);
        }
      });
      return;
    }

    if (action === 'delete') {
      const pending: PendingRetentionAction = {
        id: row.id,
        action: 'delete',
        name: row.name || row.id,
      };
      openVaultConfirmDialog(
        this.dialogService,
        retentionPolicyConfirmConfig('delete', pending.name),
      ).subscribe((confirmed) => {
        if (confirmed) {
          this.store.executeRetentionAction(pending);
        }
      });
    }
  }

  onDocumentTableAction(ev: unknown): void {
    const event = ev as ActionEvent<RetentionDocumentRow>;
    const row = event?.data;
    if (!row) return;

    if (event.action === 'suspend') {
      const pending: PendingRetentionAction = { id: row.id, action: 'suspend' };
      const config = retentionDocumentConfirmConfig('suspend');
      openVaultReasonConfirmDialog(this.dialogService, config).subscribe((reason) => {
        if (reason !== null) {
          this.store.executeRetentionAction(pending, reason);
        }
      });
      return;
    }

    if (event.action === 'resume') {
      const pending: PendingRetentionAction = { id: row.id, action: 'resume' };
      openVaultConfirmDialog(
        this.dialogService,
        retentionDocumentConfirmConfig('resume'),
      ).subscribe((confirmed) => {
        if (confirmed) {
          this.store.executeRetentionAction(pending);
        }
      });
    }
  }

  startEdit(row: RetentionPolicyRow): void {
    this.store.startEdit(row);
    afterNextRender(
      () =>
        scrollVaultEditPanelWhenReady(() => this.editPanel()?.nativeElement ?? null),
      { injector: this.injector },
    );
  }

  saveEdit(): void {
    this.store.saveEdit();
  }

  cancelEdit(): void {
    this.store.cancelEdit();
  }

  closePolicyDetail(): void {
    this.store.closePolicyDetail();
  }

  policyDetailFields(row: RetentionPolicyRow) {
    return buildRetentionPolicyDetailFields(row);
  }

  toInputValue(value: number): string {
    return this.store.toInputValue(value);
  }
}
